import React, { useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { formatTimeTo12HourUtil } from '../utils/formatTimeTo12HourUtil';

const TYPE_META = {
  service: {
    label: 'Service',
    badge: 'bg-blue-100 text-blue-700 border-blue-300',
  },

  inspection: {
    label: 'Inspection',
    badge: 'bg-green-100 text-green-700 border-green-300',
  },

  wip: {
    label: 'WIP',
    badge: 'bg-purple-100 text-purple-700 border-purple-300',
  },

  operation: {
    label: 'Operation',
    badge: 'bg-orange-100 text-orange-700 border-orange-300',
  },
};

function formatDate(value) {
  if (!value) return '—';

  const match = String(value).slice(0, 10).match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (!match) {
    return value;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const FullSchedulerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { type, id } = useParams();
  const [searchParams] = useSearchParams();

  const selectedDate = searchParams.get('date') || '';

  const storageKey =
    `fullSchedulerDetails:${type}:${id}:${selectedDate}`;

  const stored = useMemo(() => {
    try {
      const value = sessionStorage.getItem(storageKey);

      return value
        ? JSON.parse(value)
        : null;
    } catch (error) {
      console.error(
        'Unable to restore scheduler details',
        error
      );

      return null;
    }
  }, [storageKey]);

  const event =
    location.state?.event ||
    stored?.event ||
    null;

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">

        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">

          <h1 className="text-xl font-semibold text-gray-900">
            Schedule Details
          </h1>

          <p className="mt-3 text-sm text-gray-500">
            The schedule information is no longer available.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-5 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
          >
            Back to Scheduler
          </button>

        </div>

      </div>
    );
  }

  const meta =
    TYPE_META[event.type] || {
      label: event.type,
      badge: 'bg-gray-100 text-gray-700 border-gray-300',
    };

  const techNotes =
    String(event.tech_notes || '').trim();

  const internalNotes =
    String(
      event.notes ||
      event.user_comment ||
      ''
    ).trim();

  const status =
    event.STATUS ||
    event.status_raw ||
    '';

  return (
    <div className="min-h-screen bg-gray-50 pb-10">

      {/* HEADER */}
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur">

        <div className="mx-auto max-w-4xl px-4 py-3">

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-2 text-sm font-semibold text-gray-500 hover:text-gray-900"
          >
            ← Back to Scheduler
          </button>

          <div className="flex items-start justify-between gap-3">

            <div className="min-w-0">

              <div className="mb-2 flex flex-wrap items-center gap-2">

                <span
                  className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.badge}`}
                >
                  {meta.label}
                </span>

                {status && (
                  <span className="inline-flex rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold capitalize text-green-800">
                    {String(status).toLowerCase()}
                  </span>
                )}

              </div>

              <h1 className="text-xl font-bold text-gray-900 md:text-2xl">
                {event.job_site || 'No Address'}
              </h1>

              {event.customer && (
                <div className="mt-1 text-sm text-gray-500 md:text-base">
                  {event.customer}
                </div>
              )}

              <div className="mt-2 text-xs text-gray-400">
                ID: {event.id}
              </div>

            </div>

          </div>

        </div>

      </div>

      <div className="mx-auto max-w-4xl space-y-4 px-4 py-5">

        {/* DATE / TIME */}
        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">

          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Schedule
          </h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

            <div>
              <div className="text-xs text-gray-400">
                Scheduled Date
              </div>

              <div className="font-semibold text-gray-900">
                {formatDate(selectedDate || event.date_from)}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-400">
                Time
              </div>

              <div className="font-semibold text-gray-900">
                {event.start_time
                  ? formatTimeTo12HourUtil(event.start_time)
                  : '—'}

                {event.end_time
                  ? ` – ${formatTimeTo12HourUtil(event.end_time)}`
                  : ''}
              </div>
            </div>

          </div>

          {event.date_from &&
            event.date_to &&
            event.date_from !== event.date_to && (
              <div className="mt-4 border-t border-gray-100 pt-4">

                <div className="text-xs text-gray-400">
                  Schedule Range
                </div>

                <div className="font-medium text-gray-900">
                  {formatDate(event.date_from)}
                  {' → '}
                  {formatDate(event.date_to)}
                </div>

              </div>
            )}

        </section>

        {/* JOB NUMBER */}
        {event.job_number && (
          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">

            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Job #
            </div>

            <div className="mt-1 text-lg font-bold text-green-800">
              {event.job_number}
            </div>

          </section>
        )}

        {/* SERVICE TROUBLE */}
        {event.TROUBLE && (
          <section className="rounded-2xl border border-red-200 bg-white p-4 shadow-sm">

            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Trouble / Service Ticket
            </h2>

            <div className="whitespace-pre-wrap break-words rounded-xl bg-red-50 p-3 text-sm font-medium text-red-800">
              {event.TROUBLE}
            </div>

          </section>
        )}

        {/* TECH NOTES */}
        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">

          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Tech Notes
          </h2>

          {techNotes ? (
            <ExpandableText
              text={techNotes}
              className="border-amber-200 bg-amber-50"
            />
          ) : (
            <div className="text-sm text-gray-400">
              No tech notes for this record.
            </div>
          )}

        </section>

        {/* INTERNAL NOTES */}
        {internalNotes && (
          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">

            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Internal Note
            </h2>

            <ExpandableText
              text={internalNotes}
              className="border-blue-200 bg-blue-50"
            />

          </section>
        )}

      </div>

    </div>
  );
};

function ExpandableText({
  text,
  className = '',
}) {
  const [expanded, setExpanded] =
    useState(false);

  const content =
    String(text || '');

  const needsTruncate =
    content.length > 500;

  const visible =
    needsTruncate && !expanded
      ? `${content.slice(0, 500)}…`
      : content;

  return (
    <div>

      <div
        className={`whitespace-pre-wrap break-words rounded-xl border p-3 text-sm text-gray-800 ${className}`}
      >
        {visible}
      </div>

      {needsTruncate && (
        <button
          type="button"
          onClick={() =>
            setExpanded((value) => !value)
          }
          className="mt-2 text-sm font-semibold text-indigo-600 hover:underline"
        >
          {expanded
            ? 'Read less'
            : 'Read more'}
        </button>
      )}

    </div>
  );
}

export default FullSchedulerDetails;